import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, AlertActions, AlertContent, AlertText } from "@hortis/ui/alert";
import { Button, IconButton } from "@hortis/ui/button";
import { ButtonGroup } from "@hortis/ui/button/button-group";
import { Collapsible, CollapsibleContent } from "@hortis/ui/collapsible";
import { colors } from "@hortis/ui/colors";
import { Dialog, DialogClose, DialogTrigger } from "@hortis/ui/dialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { AlertCircle, Move, TextInput, XClose } from "@hortis/ui/icons";
import { Input } from "@hortis/ui/input";
import { toast } from "@hortis/ui/sonner";
import { Tab, TabHeader, TabList } from "@hortis/ui/tab";
import { useParams, useRouteContext } from "@tanstack/react-router";
import {
  useGetCollectionLocationGeometryQuery,
  useGetMapConfigQuery,
  useUpdateCollectionSiteMutation,
} from "generated/graphql";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Layer } from "react-map-gl";
import { CenterCollection } from "src/components/map/actions/center-collection";
import { CenterCurrentPosition } from "src/components/map/actions/center-current-position";
import { MapZoomButtons } from "src/components/map/actions/map-zoom-buttons";
import { SelectLayers } from "src/components/map/actions/select-layers";
import { BaseMapLayers } from "src/components/map/layers/base-map-layers";
import { LocationPolygonViewingLayer } from "src/components/map/location-bed-polygons/location-bed-polygon-layer";
import { parseLocationsGeometry } from "src/components/map/location-bed-polygons/parse-geometry";
import { useClusters } from "src/components/map/material-clusters/use-clusters";
import { MapOverlay } from "src/components/map/overlays/map-overlay";
import { NoInitialViewMapOverlay } from "src/components/map/overlays/no-initial-view-map-overlay";
import { SetInitialViewMapOverlay } from "src/components/map/overlays/set-initial-view-map-overlay";
import { MapDialogContent } from "src/components/map/position-field/map-position-modal";
import { useMaplibre } from "src/components/map/utils";
import { useNavSites } from "src/components/nav-layout/site-context/use-nav-sites";
import { CenterOverlay } from "src/features/collection-map/components/center-overlay";
import CustomOverlay from "src/features/collection-map/components/custom-overlay";
import { useMapOptionsStore } from "src/features/collection-map/components/map-options-store";
import { useGeometryLayers } from "src/features/collection-map/components/use-geometry-layers";
import { getCollectionGeometryDocument } from "src/features/collection-map/graphql";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { match } from "ts-pattern";
import { DiscardDialog } from "../../accession-data-form/discard-dialog";
import {
  type NumericPosition,
  type NumericPositionInput,
  numericPositionSchema,
} from "../schema";

const ErrorAlert = () => (
  <Alert color="error" data-cy="error-alert">
    <AlertCircle />
    <AlertContent>
      <AlertText>Something went wrong</AlertText>
      <AlertActions>
        <DialogClose asChild>
          <Button variant="linkgray">Close</Button>
        </DialogClose>
      </AlertActions>
    </AlertContent>
  </Alert>
);

export const MapPositionDialog = ({
  open,
  onOpenChange: _onOpenChange,
  onChange,
  value,
  children,
  siteSlug: _siteSlug,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (value: NumericPositionInput) => void;
  value: NumericPositionInput | null | undefined;
  children?: ReactNode;
  siteSlug?: string;
}) => {
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const isDesktop = useMediaQuery("md");
  const form = useForm<Partial<NumericPosition>>({
    mode: "onBlur",
    resolver: zodResolver(numericPositionSchema),
  });
  const formLatitude = form.watch("latitude");
  const formLongitude = form.watch("longitude");
  const { locationPolygonsEnabled } = useMapOptionsStore();

  useEffect(() => {
    if (!open) {
      form.reset({
        latitude: value?.latitude,
        longitude: value?.longitude,
        elevation: value?.elevation ?? undefined,
      });
    }
  }, [open, form, value]);

  const { "dialog-map": dialogMap } = useMaplibre();

  const {
    clusterLayer: [clusterLayer, clusterConfig],
    clusterCountLayer: [clusterCountLayer],
    unclusteredPointLayer: [unclusteredPointLayer, unclusteredPointConfig],
  } = useClusters({
    mapId: "preview-map",
    mode: "editMaterial",
  });
  const [__, updateSite] = useUpdateCollectionSiteMutation();
  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ strict: false });
  const settingsSites = useNavSites();
  const slug = siteSlug ?? settingsSites.activeSite?.urlSlug;
  const [{ data, fetching, error }] = useGetMapConfigQuery({
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: slug ?? "",
    },
    pause: slug == null,
  });
  const {
    data: geometry,
    isFetching: geometryLoading,
    error: geometryError,
  } = useGqlQuery({
    query: getCollectionGeometryDocument,
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug ?? _siteSlug ?? "",
      filters: { excludeAbsentMaterial: true, withCoordinates: true },
    },
    enabled: siteSlug != null,
  });
  const [geometryLayers] = useGeometryLayers({
    geometry,
    loading: geometryLoading,
    error: geometryError,
    layers: [clusterLayer, clusterCountLayer, unclusteredPointLayer],
    layerConfigs: [unclusteredPointConfig, clusterConfig],
  });

  const site = data?.org?.site;
  const baseMap = site?.baseMap ?? null;
  const initialView = useMemo(
    () =>
      site?.mapCenterLatitude != null &&
      site.mapCenterLongitude != null &&
      site.mapZoomLevel != null
        ? {
            latitude: site.mapCenterLatitude,
            longitude: site.mapCenterLongitude,
            zoom: site.mapZoomLevel,
          }
        : null,
    [site],
  );

  const [{ data: locationGeometryData }] =
    useGetCollectionLocationGeometryQuery({
      variables: {
        organisationSubdomain: subdomain,
        collectionSiteSlug: slug ?? "",
      },
      pause: slug == null,
    });
  const parsedLocationGeometry = parseLocationsGeometry(
    locationGeometryData?.org?.site?.locationsGeometry,
  );

  const [mode, setMode] = useState<
    "setPosition" | "noIntitialView" | "setInitialView"
  >("setPosition");

  useEffect(() => {
    if (initialView == null && !fetching) {
      setMode("noIntitialView");
    } else {
      setMode("setPosition");
    }
  }, [initialView, fetching]);

  const numericValue =
    value?.latitude == null
      ? null
      : {
          latitude: Number(value.latitude),
          longitude: Number(value.longitude),
        };
  const positioned = value != null && numericValue != null;

  const [positionTab, setPositionTab] = useState<"map" | "coordinates">("map");

  const onCoordinatePaste = async () => {
    if (!("clipboard" in navigator)) {
      return;
    }
    const clipboardText = await navigator.clipboard.readText();
    const [clipboardLat, clipboardLong] = clipboardText
      .replace(" ", "")
      .split(",");

    if (
      clipboardLat == null ||
      clipboardLong == null ||
      Number.isNaN(Number(clipboardLat)) ||
      Number.isNaN(Number(clipboardLong))
    ) {
      return;
    }

    form.setValue("latitude", Number(clipboardLat));
    form.setValue("longitude", Number(clipboardLong));
  };

  const handleSubmit = () => {
    match(positionTab)
      .with("map", () => {
        if (dialogMap == null) {
          toast.error("Failed to get map instance");
          return;
        }
        const { lat, lng } = dialogMap.getCenter();
        onChange({
          latitude: lat,
          longitude: lng,
          elevation: value?.elevation,
        });
        _onOpenChange(false);
        setDiscardDialogOpen(false);
      })
      .with("coordinates", () => {
        void form.handleSubmit((_data) => {
          const data = _data as NumericPosition;
          onChange({
            latitude: data.latitude,
            longitude: data.longitude,
            elevation: data.elevation,
          });
          _onOpenChange(false);
          setDiscardDialogOpen(false);
        })();
      })
      .exhaustive();
  };

  const onOpenChange = (open: boolean) => {
    if (
      !open &&
      !discardDialogOpen &&
      Object.keys(form.formState.dirtyFields).length > 0
    ) {
      setDiscardDialogOpen(true);
    } else {
      _onOpenChange(open);
      setDiscardDialogOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children != null && (
        <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
          {children}
        </DialogTrigger>
      )}
      <MapDialogContent
        useSiteCoordinates={!positioned}
        dragPan={positionTab === "map"}
        cursor={
          positionTab === "coordinates"
            ? "not-allowed"
            : // Have to cast here due to types not reflecting nullable value
              (null as unknown as undefined)
        }
        initialPosition={
          numericValue == null
            ? null
            : { ...numericValue, zoom: mode === "setPosition" ? 16 : 2 }
        }
        zoom={mode === "setPosition" ? 16 : 2}
        onMoveEnd={() => {
          if (
            mode === "setPosition" &&
            dialogMap != null &&
            positionTab === "map" &&
            (formLatitude != null || formLongitude != null)
          ) {
            form.setValue("latitude", dialogMap.getCenter().lat);
            form.setValue("longitude", dialogMap.getCenter().lng);
          }
        }}
      >
        <BaseMapLayers baseMap={baseMap} />
        {error == null ? (
          match(mode)
            .with("noIntitialView", () => (
              <NoInitialViewMapOverlay
                setInitialView={() => {
                  setMode("setInitialView");
                }}
              />
            ))
            .with("setInitialView", () => (
              <SetInitialViewMapOverlay
                className="rounded-2xl"
                baseMap={baseMap}
                onClose={() => {
                  setMode("setInitialView");
                  onOpenChange(false);
                }}
                confirmInitialView={async () => {
                  if (dialogMap == null) {
                    toast.error("Failed to get map instance");
                    return;
                  }
                  if (site == null) {
                    toast.error("Failed to get site");
                    return;
                  }
                  const center = dialogMap.getCenter();
                  const zoom = dialogMap.getZoom();
                  const res = await updateSite({
                    id: site.id,
                    updates: {
                      mapCenterLongitude: center.lng,
                      mapCenterLatitude: center.lat,
                      mapZoomLevel: zoom,
                    },
                  });
                  if (res.data?.updateCollectionSite?.success !== true) {
                    toast.error("Failed to update site");
                    return;
                  }
                  setMode("setPosition");
                }}
              />
            ))
            // eslint-disable-next-line sonarjs/cognitive-complexity
            .with("setPosition", () => (
              <MapOverlay className="flex-1 p-0 md:flex-col md:p-4">
                {geometryLayers}
                <Layer
                  id="hover-label"
                  type="symbol"
                  source="geometry"
                  filter={["!", ["has", "point_count"]]}
                  layout={{
                    "text-field": ["get", "materialNumber", ["adHoc"]],
                    "text-size": 12,
                    "text-offset": [0, 1.5],
                    "text-anchor": "top",
                  }}
                  paint={{
                    "text-color": colors.primary[700],
                    "text-halo-color": colors.white,
                    "text-halo-width": 1,
                  }}
                />
                {locationPolygonsEnabled && (
                  <LocationPolygonViewingLayer
                    geometry={parsedLocationGeometry}
                  />
                )}
                <CustomOverlay>
                  <CenterOverlay hideCircle={positionTab === "coordinates"} />
                </CustomOverlay>
                <div className="pointer-events-none flex flex-1 flex-row-reverse items-start gap-2 self-end p-3 md:flex-col md:items-end md:gap-4 md:p-0 lg:self-auto">
                  <DialogClose asChild>
                    <IconButton
                      icon={XClose}
                      ariaLabel="Close map"
                      className="border-transparent p-2 shadow-md md:p-2.5"
                      testId="close-map-button"
                      size="sm"
                    />
                  </DialogClose>
                  <SelectLayers baseMap={baseMap} />
                  {positionTab === "map" && (
                    <ButtonGroup
                      direction={isDesktop ? "column" : "row"}
                      className="border-none shadow-md"
                    >
                      {initialView != null && (
                        <CenterCollection {...initialView} />
                      )}
                      <CenterCurrentPosition />
                    </ButtonGroup>
                  )}
                  <MapZoomButtons map={dialogMap} />
                </div>
                <FormProvider {...form}>
                  <form className="pointer-events-auto z-10 flex w-full flex-col overflow-hidden rounded-xl bg-white p-0! shadow-lg md:max-w-sm md:self-start">
                    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
                      <div className="space-y-1">
                        <div className="text-lg font-medium text-grey-900">
                          {value == null ? "Set position" : "Change position"}
                        </div>
                        <div className="text-sm text-grey-500">
                          {match(positionTab)
                            .with(
                              "map",
                              () => "Drag the map to position material",
                            )
                            .with(
                              "coordinates",
                              () => "Enter coordinates to position material",
                            )
                            .exhaustive()}
                        </div>
                      </div>
                      <Tab value={positionTab} onValueChange={setPositionTab}>
                        <TabList>
                          <TabHeader value="map" data-cy="map-tab">
                            <Move className="h-4 w-4" />
                            Map
                          </TabHeader>
                          <TabHeader
                            value="coordinates"
                            data-cy="coordinates-tab"
                          >
                            <TextInput className="h-4 w-4" />
                            Coordinates
                          </TabHeader>
                        </TabList>
                      </Tab>
                    </div>
                    <Collapsible open={positionTab === "coordinates"}>
                      <CollapsibleContent>
                        <div className="flex w-full flex-col gap-5 border-t border-grey-200 px-4 py-6 text-base md:px-6">
                          <FormField
                            control={form.control}
                            name="latitude"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Latitude</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    onPaste={onCoordinatePaste}
                                    data-cy="latitude-input"
                                    {...field}
                                    onBlur={() => {
                                      if (
                                        formLongitude != null &&
                                        formLatitude != null
                                      ) {
                                        dialogMap?.flyTo({
                                          center: [formLongitude, formLatitude],
                                        });
                                      }
                                      field.onBlur();
                                    }}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                      field.onChange(
                                        e.target.value === ""
                                          ? null
                                          : Number(e.target.value),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="longitude"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Longitude</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    data-cy="longitude-input"
                                    {...field}
                                    onBlur={() => {
                                      if (
                                        formLongitude != null &&
                                        formLatitude != null
                                      ) {
                                        dialogMap?.flyTo({
                                          center: [formLongitude, formLatitude],
                                        });
                                      }
                                      field.onBlur();
                                    }}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                      field.onChange(
                                        e.target.value === ""
                                          ? null
                                          : Number(e.target.value),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="elevation"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Elevation</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    data-cy="elevation-input"
                                    {...field}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                      field.onChange(
                                        e.target.value === ""
                                          ? null
                                          : Number(e.target.value),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                    <div className="flex justify-end gap-3 border-t border-grey-200 px-4 py-3 md:px-6">
                      <DiscardDialog
                        onDiscard={() => {
                          onOpenChange(false);
                        }}
                        open={discardDialogOpen}
                        onOpenChange={setDiscardDialogOpen}
                      >
                        <DialogTrigger
                          asChild
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onOpenChange(false);
                          }}
                        >
                          <Button
                            variant="tertiaryGray"
                            testId="cancel-position-button"
                          >
                            Cancel
                          </Button>
                        </DialogTrigger>
                      </DiscardDialog>
                      <Button
                        data-cy="confirm-update-position"
                        variant="primary"
                        type="submit"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSubmit();
                        }}
                      >
                        Confirm
                      </Button>
                    </div>
                  </form>
                </FormProvider>
              </MapOverlay>
            ))
            .exhaustive()
        ) : (
          <MapOverlay className="items-center justify-center p-4">
            <ErrorAlert />
          </MapOverlay>
        )}
      </MapDialogContent>
    </Dialog>
  );
};
